import styled from 'styled-components/macro'

import { IconExport } from '@loadsmart/icons'
import { Dialog } from '@loadsmart/loadsmart-ui'
import { getToken } from '@loadsmart/loadsmart-ui/dist/theming'
import { Text, Layout } from '@loadsmart/miranda-react'

import ImgCarrierOnboardingRMIS from 'legacy/assets/svg/carrier-onboarding-rmis.svg'

import { OnboardingStep } from '../../domain/RMISOnboarding'

type OnboardingStepLabels = {
  title: string | JSX.Element
  body: string | JSX.Element
  actions?: {
    confirm?: string | JSX.Element
    cancel?: string | JSX.Element
  }
}

type RMISOnboardingDialogProps = {
  onCancel: () => void
  onConfirm: () => void
  step: OnboardingStep
}

const StyledIconExport = styled(IconExport)`
  width: ${getToken('font-size-3')};
  height: ${getToken('font-size-3')};
  margin-right: ${getToken('space-s')};
`

const StyledTextContent = styled(Text)`
  text-align: left;
`

const onboardingStepLabels: Record<OnboardingStep, OnboardingStepLabels> = {
  [OnboardingStep.Pending]: {
    title: 'Validate your documents in RMIS',
    body: (
      <>
        Before booking this load you need to upload your documents in RMIS.
        <Text variant="body-md-bold"> It's easy and takes less than 5 minutes.</Text>
      </>
    ),
    actions: {
      confirm: (
        <>
          <StyledIconExport />
          Go To RMIS
        </>
      ),
      cancel: 'Cancel',
    },
  },
  [OnboardingStep.UnderReview]: {
    title: 'We are validating your documents',
    body: (
      <Layout.Stack>
        <Text variant="body-md">
          Hang tight! We are currently processing your documents and will get back to you as soon as
          they have been validated.
        </Text>
        <Text variant="body-md-bold">
          If you don't receive an answer in 2 business days, please contact our support.
        </Text>
      </Layout.Stack>
    ),
    actions: {
      confirm: 'Ok',
    },
  },
}

export function RMISOnboardingDialog({ onCancel, onConfirm, step }: RMISOnboardingDialogProps) {
  const { title, body, actions } = onboardingStepLabels[step]

  return (
    <Dialog onOverlayClick={onCancel} open data-testid="RMISOnboardingDialog">
      <Dialog.Close onClick={onCancel} />
      <Dialog.Header>{title}</Dialog.Header>
      <Dialog.Body>
        <Layout.Stack align="center" gap="spacing-8">
          <img src={ImgCarrierOnboardingRMIS} alt="RMIS Onboarding" width="238px" height="189px" />
          <StyledTextContent variant="body-md">{body}</StyledTextContent>
        </Layout.Stack>
      </Dialog.Body>
      {actions && (
        <Dialog.Actions direction={actions.cancel ? 'horizontal' : 'vertical'} scale="large">
          {actions.confirm && (
            <Dialog.ActionConfirm onConfirm={onConfirm}>{actions.confirm}</Dialog.ActionConfirm>
          )}
          {actions.cancel && (
            <Dialog.ActionCancel onCancel={onCancel}>{actions.cancel}</Dialog.ActionCancel>
          )}
        </Dialog.Actions>
      )}
    </Dialog>
  )
}
